/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator, ValidatorFn } from '@angular/forms';
import { formatAsString } from 'app/shared/helpers/date-helpers';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import moment from 'moment';

const customValueProvider = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => DatetimePickerComponent),
	multi: true,
};
const customValidateProvider = {
	provide: NG_VALIDATORS,
	useExisting: forwardRef(() => DatetimePickerComponent),
	multi: true,
};

@Component({
	selector: 'date-time-picker',
	templateUrl: './date-time-picker.component.html',
	styleUrls: ['./date-time-picker.component.scss'],
	providers: [customValueProvider, customValidateProvider],
	standalone: false,
})
export class DatetimePickerComponent implements ControlValueAccessor, OnChanges, Validator {
	private readonly displayDateTimeFormat = 'DD/MM/YYYY HH:mm';
	private readonly compactDateTimeDigitCount = 12;

	dataForm: FormGroup;
	@Input() startTabIndex = 0;
	@Input() label: string = 'Date and Time';
	@Input() isValid: boolean = true;
	@Input() isDisabled: boolean = false;
	@Output() dateTimeExited = new EventEmitter<void>();

	writtenValue: Date | null | undefined;
	private isSynchronisingControls = false;
	private preserveInvalidDisplayValue = false;
	private propagateValidatorChange: () => void = () => {};

	constructor(
		fb: FormBuilder,
		private formHelperService: ReactiveFormHelperService,
	) {
		this.dataForm = fb.group({
			date: [{ value: '', disabled: this.isDisabled }, [this.isValidValidator()]],
			time: [{ value: '', disabled: this.isDisabled }, [this.isValidValidator()]],
			display: [{ value: '', disabled: this.isDisabled }, [this.isValidValidator(), this.dateTimeFormatValidator()]],
		});

		this.date.valueChanges.subscribe(() => this.onPickerValueChanged());
		this.time.valueChanges.subscribe(() => this.onPickerValueChanged());
		this.display.valueChanges.subscribe((value) => this.onDisplayValueChanged(value));
	}

	// When the invalid input changes refresh the validity of the date and time controls and check for disabled
	ngOnChanges(changes: SimpleChanges): void {
		if (changes.isDisabled) {
			if (this.isDisabled) {
				this.dataForm.get('date')?.disable();
				this.dataForm.get('time')?.disable();
				this.dataForm.get('display')?.disable();
			} else {
				this.dataForm.get('date')?.enable();
				this.dataForm.get('time')?.enable();
				this.dataForm.get('display')?.enable();
			}
		}
		if (changes.isValid) {
			this.date.updateValueAndValidity({ emitEvent: false });
			this.time.updateValueAndValidity({ emitEvent: false });
			this.display.updateValueAndValidity({ emitEvent: false });
		}
	}

	/**
	 * Can be called when save is clicked on the parent form to mark child controls as touched and update validaiton.
	 * This appears to be necessary to resolve an issue in which the error box outline wasn't appearing if save is clicked before touching the controls.
	 */
	markAsTouchedAndUpdateValueAndValidity() {
		this.date.markAsTouched();
		this.time.markAsTouched();
		this.display.markAsTouched();
		this.markAsTouched();

		// Validation during save must not emit valueChanges. Emitting here runs the
		// picker synchronisation subscriptions and can replace raw invalid text with
		// the last valid values held by the hidden date and time controls.
		this.date.updateValueAndValidity({ emitEvent: false });
		this.time.updateValueAndValidity({ emitEvent: false });
		this.display.updateValueAndValidity({ emitEvent: false });
		this.propagateValidatorChange();
	}

	markAsUntouched() {
		this.date.markAsUntouched();
		this.time.markAsUntouched();
		this.display.markAsUntouched();
	}

	handleExit() {
		this.markAsTouched();
		this.dateTimeExited.emit();
	}

	get date(): AbstractControl {
		return this.dataForm.controls['date'];
	}
	get time(): AbstractControl {
		return this.dataForm.controls['time'];
	}
	get display(): AbstractControl {
		return this.dataForm.controls['display'];
	}

	// Picker inputs remain separate because Angular Material does not support attaching
	// MatDatepickerInput and MatTimepickerInput to the same native input. They are kept
	// in sync with the single visible date/time input.
	private onPickerValueChanged() {
		if (this.isSynchronisingControls) {
			return;
		}

		const datePart = this.getDateDisplayString(this.date.value);
		const timePart = this.getTimeString(this.time.value);
		const displayValue = [datePart, timePart].filter((part) => !!part).join(' ');
		this.preserveInvalidDisplayValue = !!displayValue && !(datePart && timePart);

		this.isSynchronisingControls = true;
		this.display.setValue(displayValue, { emitEvent: false });
		this.isSynchronisingControls = false;

		this.propagateCombinedValue();
		this.propagateValidatorChange();
	}

	private onDisplayValueChanged(value: string | null) {
		if (this.isSynchronisingControls) {
			return;
		}

		const rawValue = value ?? '';
		const maskedValue = this.applyProgressiveDateTimeMask(rawValue);
		if (maskedValue !== rawValue) {
			this.isSynchronisingControls = true;
			this.display.setValue(maskedValue, { emitEvent: false });
			this.isSynchronisingControls = false;
		}

		const trimmedValue = maskedValue.trim();
		if (!trimmedValue) {
			this.preserveInvalidDisplayValue = false;
			this.setPickerControls(null);
			this.propagateChange(null);
			this.propagateValidatorChange();
			return;
		}

		const parsedDateTime = this.parseDisplayDateTime(trimmedValue);
		if (!parsedDateTime.isValid()) {
			// The parent owns validation. Publish null so its required validator fails,
			// but preserve the user's raw text instead of replacing it with the model value.
			this.preserveInvalidDisplayValue = true;
			this.propagateChange(null);
			this.propagateValidatorChange();
			return;
		}

		this.preserveInvalidDisplayValue = false;
		this.setPickerControls(parsedDateTime);
		this.propagateChange(parsedDateTime.toDate());
		this.propagateValidatorChange();
	}

	handleDisplayInput(event: Event) {
		if (!(event.target instanceof HTMLInputElement)) {
			return;
		}

		const nativeValue = event.target.value;
		if (nativeValue === this.display.value) {
			return;
		}

		// The native input event can run before the FormControl directive has copied
		// a mouse-selected character replacement into the control. Synchronise and
		// process that exact browser value immediately.
		this.display.setValue(nativeValue, { emitEvent: false });
		this.onDisplayValueChanged(nativeValue);
	}

	private applyProgressiveDateTimeMask(value: string): string {
		// Preserve unsupported characters and overlong values so validation can
		// report them instead of silently changing the user's input.
		if (!/^[\d/:\s]*$/.test(value)) {
			return value;
		}

		// A complete separator structure indicates that the user is editing an
		// existing date/time. Preserve its sections and the caret position. A
		// partially applied mask must continue through the year and time sections.
		if (/^\d{0,2}\/\d{0,2}\/\d{0,4}\s+\d{0,2}:\d{0,2}$/.test(value)) {
			return value;
		}

		const digits = value.replace(/\D/g, '');
		if (!digits && value.trim()) {
			return value;
		}
		if (digits.length > this.compactDateTimeDigitCount) {
			return value;
		}

		let maskedValue = digits.slice(0, 2);
		if (digits.length > 2) {
			maskedValue += `/${digits.slice(2, 4)}`;
		}
		if (digits.length > 4) {
			maskedValue += `/${digits.slice(4, 8)}`;
		}
		if (digits.length > 8) {
			maskedValue += ` ${digits.slice(8, 10)}`;
		}
		if (digits.length > 10) {
			maskedValue += `:${digits.slice(10, 12)}`;
		}

		return maskedValue;
	}

	private normaliseSectionedDateTime(value: string): string {
		const sections = value.match(/^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2})\s*$/);
		if (!sections) {
			return value;
		}

		const [, day, month, year, hour, minute] = sections;
		return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year} ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
	}

	private parseDisplayDateTime(value: string): moment.Moment {
		return moment(value, this.displayDateTimeFormat, true);
	}

	private setPickerControls(value: moment.Moment | null) {
		this.isSynchronisingControls = true;
		this.date.setValue(value?.clone() ?? null, { emitEvent: false });
		this.time.setValue(value?.clone() ?? null, { emitEvent: false });
		this.isSynchronisingControls = false;
	}

	private propagateCombinedValue() {
		const datePart = this.getDateString(this.date.value);
		const timePart = this.getTimeString(this.time.value);

		if (datePart && timePart) {
			this.propagateChange(moment(`${datePart}T${timePart}`).toDate());
		} else if (!datePart && !timePart) {
			this.propagateChange(null);
		}
	}

	private getDateDisplayString(value: any): string | null {
		const parsedDate = moment(value);
		return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : null;
	}

	private getDateString(value: any): string | null {
		const parsedDate = moment(value);
		return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
	}

	private getTimeString(value: any): string | null {
		if (typeof value === 'string') {
			const parsedTime = moment(value, 'HH:mm', true);
			return parsedTime.isValid() ? parsedTime.format('HH:mm') : null;
		}

		const parsedTime = moment(value);
		return parsedTime.isValid() ? parsedTime.format('HH:mm') : null;
	}

	propagateChange: any = () => {};
	onTouched: any = () => {
		console.log('touched');
	};

	writeValue(date: Date | null | undefined): void {
		this.writtenValue = date;
		if (this.preserveInvalidDisplayValue && date == null) {
			return;
		}

		this.preserveInvalidDisplayValue = false;
		this.setControlsFromWrittenValue();
		this.date.markAsPristine();
		this.time.markAsPristine();
		this.display.markAsPristine();
	}

	private setControlsFromWrittenValue() {
		const dateTime = this.writtenValue ? moment(this.writtenValue) : null;
		const value = dateTime?.isValid() ? dateTime : null;

		this.setPickerControls(value);
		this.display.setValue(value?.format(this.displayDateTimeFormat) ?? '', { emitEvent: false });

		this.date.markAsPristine();
		this.time.markAsPristine();
		this.display.markAsPristine();
	}

	handleDisplayExit(event?: FocusEvent) {
		const inputValue = event?.target instanceof HTMLInputElement ? event.target.value : (this.display.value ?? '');
		const normalisedValue = this.normaliseSectionedDateTime(inputValue);

		// Mouse-selection replacement can update the native input before Angular's
		// FormControl reflects it. Use the DOM text as the final source of truth.
		if (normalisedValue !== this.display.value) {
			this.display.setValue(normalisedValue, { emitEvent: false });
		}

		const parsedDateTime = this.parseDisplayDateTime(normalisedValue);
		if (parsedDateTime.isValid()) {
			// Commit from the final visible text before notifying the parent that the
			// field was exited. This also covers single-character replacements where an
			// intermediate invalid value may previously have published null.
			this.preserveInvalidDisplayValue = false;
			this.setPickerControls(parsedDateTime);
			this.display.setValue(parsedDateTime.format(this.displayDateTimeFormat), { emitEvent: false });
			this.propagateChange(parsedDateTime.toDate());
		} else {
			this.preserveInvalidDisplayValue = !!normalisedValue.trim();
			this.propagateChange(null);
		}
		this.propagateValidatorChange();
		this.handleExit();
	}

	registerOnChange(fn: any): void {
		this.propagateChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	validate(control: AbstractControl): { invalidDateTime: true } | null {
		return this.getDateTimeFormatError(this.display.value);
	}

	private dateTimeFormatValidator(): ValidatorFn {
		return (control: AbstractControl): { invalidDateTime: true } | null => this.getDateTimeFormatError(control.value);
	}

	private getDateTimeFormatError(rawValue: string | null | undefined): { invalidDateTime: true } | null {
		const value = rawValue?.trim();
		if (!value) {
			return null;
		}

		return this.parseDisplayDateTime(value).isValid() ? null : { invalidDateTime: true };
	}

	registerOnValidatorChange(fn: () => void): void {
		this.propagateValidatorChange = fn;
	}

	isValidValidator(): ValidatorFn {
		return (group: FormGroup): { [key: string]: any } => {
			return !this.isValid ? { invalid: true } : null;
		};
	}

	// Marks the control as pristine if the value has not changed from the original value.
	// This method should be called from the parent control 'cancel' method to prevent the 'confirmation dialog' from appearing
	markAsPristineIfUnchanged(control: AbstractControl, originalValue: Date) {
		const originalEventDateAndTime = formatAsString(moment(originalValue), 'YYYY-MM-DDTHH:mm:ss');
		var currentEventDateAndTime = formatAsString(control.value, 'YYYY-MM-DDTHH:mm:ss');

		var dateInvalid = !moment(this.date.value).isValid();
		var timeInvalid = !moment(this.time.value).isValid();
		var bothInvalid = dateInvalid && timeInvalid;

		// If both are null, we consider them unchanged, 'undefined' is the same as 'null' in this context
		// both invalid is being checked here because when only one part of the date/time is entered, the control value is null
		// and this is causing the cancel confirmation dialog to not appear when only one part of the date/time is changed.
		var bothAreNull = originalValue == null && control.value == null && bothInvalid;

		if (currentEventDateAndTime == originalEventDateAndTime || bothAreNull) {
			control.markAsPristine();
		}
	}

	markAsTouched() {
		this.onTouched();
	}
}
